/**
 * UserProfilePage — V2 orchestrator for the User profile.
 *
 * Manages: session/permissions, two polling cycles (basic 600s, reporting 1200s),
 * tab state (basic, reporting, logs), drawers, dialogs, toast.
 *
 * Props: id — User ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './user-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../components/v2/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import ProfileReportingTab from '../../../components/v2/ProfileReportingTab.jsx';
import LogsTab from './components/LogsTab.jsx';
import ResetPasswordDialog from '../components/ResetPasswordDialog.jsx';
import ConfirmTypedDialog from '../../../components/v2/ConfirmTypedDialog.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import EventNoteDialog from '../../../components/v2/EventNoteDialog.jsx';

import {
    getSession,
    setSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getRoleWeight,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, getToday, addDaysToDate, shiftDateStr } from '../../../libs.jsx';
import {
    fetchUserProfile, fetchUserRoles, fetchUserLogs, fetchUserBadges,
    fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
    fetchPreformNotes,
    editUserProfile, resetUserPassword, deleteUser, addEventNote,
} from './api/userProfileApi.js';
import { fetchCallLogsReport } from '../../reports/call-logs/api/callLogsReportApi.js';

const UserProfilePage = ({ id }) => {
    const navigate = useNavigate();
    const userId = id;

    // ======================================================================
    // SESSION & PERMISSIONS
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);
    const isCloud = Boolean(campusConfig?.is_cloud);
    // Mirrors UsersPage.jsx: tag the edit payload with the campus's grandstream
    // flag so the backend can decide whether to (de)provision a SIP extension.
    const isGrandstream = Boolean(campusConfig?.is_grandstream);
    const myRoleWeight = getRoleWeight();
    const editRights = iAmHigherAuthority(10, myRoleWeight);
    const showAdminTabs = iAmHigherAuthority(80, myRoleWeight);
    const eventNotesActive = Boolean(campusConfig?.event_notes_active);
    const eventNotesPreform = Boolean(campusConfig?.event_notes_preform);
    const eventNotesFreeform = Boolean(campusConfig?.event_notes_freeform);

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [basicData, setBasicData] = useState(null);
    const [roleData, setRoleData] = useState([]);
    const [logData, setLogData] = useState([]);
    const [badgeData, setBadgeData] = useState([]);
    const [hourData, setHourData] = useState({});
    const [weekData, setWeekData] = useState({});
    const [reasonData, setReasonData] = useState({});
    const [eventData, setEventData] = useState([]);
    const [prenoteData, setPrenoteData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Derived
    const displayName = basicData?.display_name || '';
    const username = basicData?.username || '';
    const userRoleWeight = parseInt(basicData?.user_role_weight || 0, 10);
    const higherRole = iAmHigherAuthority(userRoleWeight, myRoleWeight);
    const userGroups = basicData?.groups || [];

    // ======================================================================
    // TAB STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('basic');

    // ======================================================================
    // DRAWER/DIALOG STATE
    // ======================================================================
    const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
    const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteEventIndex, setNoteEventIndex] = useState(null);
    const [saving, setSaving] = useState(false);

    // ======================================================================
    // TOAST
    // ======================================================================
    const { toast, showToast, dismissToast } = useToast(6000);

    // ======================================================================
    // DATA LOADING — Cycle 1: Basic (600s)
    // ======================================================================
    const loadBasicData = useCallback(async () => {
        if (!campusId || !userId) return;
        setLoading(true);
        try {
            const [profile, roles, logs, badges] = await Promise.all([
                fetchUserProfile(campusId, userId),
                fetchUserRoles(campusId),
                showAdminTabs ? fetchUserLogs(userId) : Promise.resolve([]),
                fetchUserBadges(campusId, userId),
            ]);
            setBasicData(Array.isArray(profile) && profile.length > 0 ? profile[0] : null);
            setRoleData(Array.isArray(roles) ? roles : []);
            setLogData(Array.isArray(logs) ? logs : []);
            setBadgeData(Array.isArray(badges) ? badges : []);
        } catch (err) {
            console.error('[UserProfilePage] loadBasicData error:', err);
        } finally {
            setLoading(false);
        }
    }, [campusId, userId, showAdminTabs]);

    useEffect(() => { loadBasicData(); }, [loadBasicData]);

    // Polling: basic cycle
    useEffect(() => {
        const timer = setInterval(loadBasicData, 600000);
        return () => clearInterval(timer);
    }, [loadBasicData]);

    // ======================================================================
    // DATA LOADING — Cycle 2: Reporting (1200s)
    // ======================================================================
    const loadReportingData = useCallback(async () => {
        if (!campusId || !userId) return;
        try {
            const today = getToday();
            const startDate = formatDateIso(addDaysToDate(today, -28));
            const endDate = formatDateIso(today);

            const [hour, week, reason, events, prenotes] = await Promise.all([
                fetchHourChart(campusId, userId),
                fetchWeekChart(campusId, userId),
                fetchReasonSummary(campusId, userId, startDate, endDate),
                fetchLimitedEvents(campusId, userId),
                eventNotesActive ? fetchPreformNotes(campusId) : Promise.resolve([]),
            ]);
            let eventList = Array.isArray(events) ? events : [];

            // Grandstream campuses: fetch call logs for the events' date range and
            // merge them into events by event_id (mirrors AlertDetailsPage) so the
            // Recent Events table can show a per-event call count + hover.
            // Non-fatal on failure.
            if (isGrandstream && eventList.length) {
                try {
                    const dates = eventList
                        .map((e) => (e.event_datetime_local ? String(e.event_datetime_local).slice(0, 10) : ''))
                        .filter(Boolean)
                        .sort();
                    if (dates.length) {
                        const callResp = await fetchCallLogsReport({
                            campusId,
                            startDate: shiftDateStr(dates[0], -1),
                            endDate: shiftDateStr(dates[dates.length - 1], 1),
                        });
                        const callRecords = callResp?.records || [];
                        if (callRecords.length) {
                            const callsByEvent = new Map();
                            callRecords.forEach((c) => {
                                const key = String(c.event_id ?? '');
                                if (!key) return;
                                if (!callsByEvent.has(key)) callsByEvent.set(key, []);
                                callsByEvent.get(key).push(c);
                            });
                            callsByEvent.forEach((arr) => arr.sort(
                                (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
                            ));
                            eventList = eventList.map((r) => ({
                                ...r,
                                calls: callsByEvent.get(String(r.event_id ?? '')) || [],
                            }));
                        }
                    }
                } catch (err) {
                    console.error('[UserProfilePage] Call logs merge failed:', err);
                }
            }

            setHourData(hour || {});
            setWeekData(week || {});
            setReasonData(reason || {});
            setEventData(eventList);
            setPrenoteData(Array.isArray(prenotes) ? prenotes : []);
        } catch (err) {
            console.error('[UserProfilePage] loadReportingData error:', err);
        }
    }, [campusId, userId, eventNotesActive, isGrandstream]);

    useEffect(() => { loadReportingData(); }, [loadReportingData]);

    useEffect(() => {
        const timer = setInterval(loadReportingData, 1200000);
        return () => clearInterval(timer);
    }, [loadReportingData]);

    // ======================================================================
    // HANDLERS
    // ======================================================================

    // Edit basic info
    const handleSaveBasic = useCallback(async (formData) => {
        setSaving(true);
        const payload = {
            user_id: userId,
            display_name: formData.display_name,
            email: formData.email,
            phone: formData.phone,
            user_role_id: formData.user_role_id,
            title: formData.title,
            username,
            campus_id: campusId,
            cognito_id: basicData?.cognito_id,
            sip_extension: basicData?.sip_extension,
            wave_password: basicData?.wave_password,
            is_grandstream: isGrandstream,
            group_ids: userGroups.map((g) => g.group_id),
            modified_by: getUserId(),
        };
        // Keys must match what usersApi.editUser reads off oldData. This used
        // to say `groups:`, which nothing consumed, so the previous group list
        // never reached the audit entry. sip_extension rides along because
        // User_Manager.py reads it off the old payload too.
        const oldPayload = {
            user_id: userId,
            display_name: displayName,
            email: basicData?.email,
            phone: basicData?.phone,
            user_role_id: basicData?.user_role_id,
            title: basicData?.title,
            username,
            group_ids: userGroups.map((g) => g.group_id),
            campus_id: campusId,
            cognito_id: basicData?.cognito_id,
            sip_extension: basicData?.sip_extension,
            wave_password: basicData?.wave_password,
            is_grandstream: isGrandstream,
        };
        const result = await editUserProfile(payload, oldPayload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'User updated successfully.');
            setBasicDrawerOpen(false);
            // Update session if editing yourself
            if (username === session?.username) {
                const s = getSession();
                if (s) {
                    s.email = formData.email.toLowerCase();
                    s.display = formData.display_name;
                    s.title = formData.title;
                    setSession(s);
                }
            }
            loadBasicData();
        } else {
            showToast('error', result[1] || 'Failed to save.');
        }
    }, [userId, username, campusId, basicData, userGroups, displayName, isCloud, isGrandstream, session, showToast, loadBasicData]);

    // Reset password
    /* Called by the shared ResetPasswordDialog with { user_id, username, password }.
       Returns the result: the dialog shows a failure inline, so only success is
       reported here (toast + close), as on the Users list. */
    const handleResetPassword = useCallback(async ({ password: newPassword }) => {
        setSaving(true);
        const payload = { user_id: userId, username, campus_id: campusId, cognito_id: basicData?.cognito_id, password: newPassword, modified_by: getUserId() };
        const result = await resetUserPassword(payload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Password reset successfully.');
            setResetPwDialogOpen(false);
        }
        return result;
    }, [userId, username, campusId, basicData, isCloud, showToast]);

    // Delete user
    const handleDelete = useCallback(async () => {
        setSaving(true);
        // sip_extension + is_grandstream let the backend clean up the user's
        // SIP registration alongside the user record itself. Mirrors the
        // shape of deleteUsers() in src/pages/users/api/usersApi.js.
        const payload = {
            user_id: userId,
            username,
            campus_id: campusId,
            cognito_id: basicData?.cognito_id,
            sip_extension: basicData?.sip_extension,
            is_grandstream: isGrandstream,
            modified_by: getUserId(),
        };
        const result = await deleteUser(payload, isCloud, { userId: getUserId(), clientId: getSessionId() });
        setSaving(false);
        if (result[0]) {
            showToast('success', 'User deleted.');
            navigate('/users/usermanagement');
        } else {
            showToast('error', result[1] || 'Failed to delete user.');
        }
    }, [userId, username, campusId, basicData, isCloud, isGrandstream, showToast, navigate]);

    // Add event note
    const handleAddNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Note added.');
            setNoteDialogOpen(false);
            setNoteEventIndex(null);
            loadReportingData();
        } else {
            showToast('error', result[1] || 'Failed to add note.');
        }
    }, [campusId, showToast, loadReportingData]);

    // Campus change
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
    }, []);

    // ======================================================================
    // TAB CONFIG
    // ======================================================================
    const tabs = useMemo(() => {
        const t = [
            { key: 'basic', label: 'Basic Info' },
            { key: 'reporting', label: 'Reporting' },
        ];
        if (showAdminTabs) t.push({ key: 'logs', label: 'User Logs' });
        return t;
    }, [showAdminTabs]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page Header */}
                    <header className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <Link to="/users/usermanagement" className="ps-breadcrumb__text">Users</Link>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">{displayName || 'User Profile'}</span>
                                </nav>
                                <div className="ps-campus-row">
                                    <label htmlFor="upp-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="upp-campus-selector" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                            {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            {/* Lifecycle actions */}
                            {editRights && higherRole && basicData ? (
                                <div className="upp-lifecycle-actions">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setResetPwDialogOpen(true)}>
                                        <i className="fe fe-lock" style={{ marginRight: '4px' }} />Reset Password
                                    </button>
                                    <button className="dp-btn dp-btn--danger dp-btn--sm" onClick={() => setDeleteDialogOpen(true)}>
                                        <i className="fe fe-trash-2" style={{ marginRight: '4px' }} />Delete
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </header>

                    {/* Profile Banner */}
                    <ProfileBanner
                        name={displayName}
                        subtitle={basicData?.user_role_name || ''}
                        metaFields={[
                            { label: 'Username', value: username },
                            { label: 'Email', value: basicData?.email || '—' },
                            { label: 'Phone', value: basicData?.phone || '—' },
                        ]}
                        loading={loading}
                    />

                    {/* Tab Bar */}
                    <div className="pp-tab-bar">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="pp-tab-content">
                        {activeTab === 'basic' ? (
                            <BasicInfoTab
                                basicData={basicData}
                                userGroups={userGroups}
                                editRights={editRights}
                                higherRole={higherRole}
                                onEdit={() => setBasicDrawerOpen(true)}
                                isGrandstream={isGrandstream}
                                isCloud={isCloud}
                                badges={badgeData}
                                tzOffset={tzOffset}
                            />
                        ) : null}

                        {activeTab === 'reporting' ? (
                            <ProfileReportingTab
                                entity="user"
                                hourData={hourData}
                                weekData={weekData}
                                reasonData={reasonData}
                                eventData={eventData}
                                eventNotesActive={eventNotesActive}
                                isGrandstream={isGrandstream}
                                tzOffset={tzOffset}
                                onAddNote={(idx) => { setNoteEventIndex(idx); setNoteDialogOpen(true); }}
                                entityId={userId}
                            />
                        ) : null}

                        {activeTab === 'logs' && showAdminTabs ? (
                            <LogsTab logData={logData} tzOffset={tzOffset} />
                        ) : null}
                    </div>

                    {/* Edit Drawer */}
                    <BasicInfoDrawer
                        open={basicDrawerOpen}
                        onClose={() => setBasicDrawerOpen(false)}
                        onSave={handleSaveBasic}
                        saving={saving}
                        basicData={basicData}
                        roles={roleData}
                        myRoleWeight={myRoleWeight}
                    />

                    {/* Reset Password Dialog */}
                    {resetPwDialogOpen ? (
                        /* The Users list's dialog, so both screens apply the same
                           password rules (utils/passwordRules.js) and show a failed
                           reset inline. */
                        <ResetPasswordDialog
                            user={{ user_id: userId, username, display_name: displayName, user_role_id: basicData?.user_role_id }}
                            onClose={() => setResetPwDialogOpen(false)}
                            onConfirm={handleResetPassword}
                        />
                    ) : null}

                    {/* Delete — requires typing "confirm", with the same wording as
                        the Users list. */}
                    {deleteDialogOpen ? (
                        <ConfirmTypedDialog
                            title="Delete User"
                            confirmLabel="Delete"
                            savingLabel="Deleting…"
                            onClose={() => setDeleteDialogOpen(false)}
                            onConfirm={handleDelete}
                            saving={saving}
                        >
                            <p style={{ margin: 0, lineHeight: 1.6 }}>
                                Are you sure you want to delete <strong>{displayName}</strong>?
                            </p>
                            <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                Their sign-in account will be removed and they will immediately lose access to the web portal and mobile.{' '}
                                <strong>This cannot be undone.</strong>
                            </p>
                        </ConfirmTypedDialog>
                    ) : null}

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteEventIndex !== null ? (
                        <EventNoteDialog
                            event={eventData[noteEventIndex]}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
                            onSave={handleAddNote}
                            saving={saving}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default UserProfilePage;
